import type { ReactNode } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import type { AktivitaPeriod, AktivitaCountry } from '../../api/rondo';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const nf = new Intl.NumberFormat('cs-CZ');

export function fmtNum(v: number | null | undefined): string {
  return nf.format(Number(v ?? 0));
}
export function fmtNum2(v: number | null | undefined): string {
  return Number(v ?? 0).toFixed(1);
}
export function fmtDuration(sec: number | null | undefined): string {
  const s = Math.round(Number(sec ?? 0));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m} min ${r} s`;
}
export function fmtPct(v: number | null | undefined, dec = 1): string {
  return `${(Number(v ?? 0) * 100).toFixed(dec)} %`;
}

export type KpiFmt = 'num' | 'num2' | 'duration' | 'pct' | 'pct2' | 'pos';

export function fmtValue(v: number | null | undefined, fmt: KpiFmt): string {
  switch (fmt) {
    case 'num':
      return fmtNum(v);
    case 'num2':
      return fmtNum2(v);
    case 'duration':
      return fmtDuration(v);
    case 'pct':
      return fmtPct(v, 1);
    case 'pct2':
      return fmtPct(v, 2);
    case 'pos':
      return Number(v ?? 0).toFixed(1);
  }
}

export function DeltaArrow({
  curr,
  prev,
  lowerIsBetter,
}: {
  curr: number;
  prev: number;
  lowerIsBetter?: boolean;
}): ReactNode {
  if (!prev || Number.isNaN(prev)) return <span className="text-muted">-</span>;
  const pct = ((curr - prev) / prev) * 100;
  const up = pct >= 0;
  const goodIsUp = !lowerIsBetter;
  const good = up === goodIsUp;
  const cls = good ? 'text-success' : 'text-danger';
  return (
    <span className={`${cls} fw-medium`}>
      {up ? '↑' : '↓'} {up ? '+' : ''}
      {pct.toFixed(1)} %
    </span>
  );
}

export function DeltaPlain({
  curr,
  prev,
  lowerIsBetter,
}: {
  curr: number;
  prev: number;
  lowerIsBetter?: boolean;
}): ReactNode {
  if (!prev || Number.isNaN(prev)) return <span className="text-muted">-</span>;
  const pct = ((curr - prev) / prev) * 100;
  const up = pct >= 0;
  const good = up === !lowerIsBetter;
  const cls = good ? 'text-success' : 'text-danger';
  return (
    <span className={cls}>
      {up ? '+' : ''}
      {pct.toFixed(1)} %
    </span>
  );
}

export function KpiTile({
  label,
  value,
  icon,
  bg,
  fg,
  valueSize = '1.75rem',
  delta,
  prevText,
  colClass = 'col-6 col-lg-3',
}: {
  label: string;
  value: string;
  icon: string;
  bg: string;
  fg: string;
  valueSize?: string;
  delta?: ReactNode;
  prevText?: string;
  colClass?: string;
}) {
  return (
    <div className={colClass}>
      <div className="card shadow-sm h-100 kpi-tile">
        <div className="card-body">
          <div className="d-flex align-items-start justify-content-between mb-2">
            <span className="text-muted small">{label}</span>
            <span
              style={{
                background: bg,
                color: fg,
                width: 36,
                height: 36,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i data-feather={icon} style={{ width: 18, height: 18 }} />
            </span>
          </div>
          <div style={{ fontSize: valueSize, fontWeight: 600, lineHeight: 1.15 }}>{value}</div>
          {delta !== undefined && (
            <div className="small mt-2">
              {delta} {prevText !== undefined && <span className="text-muted">vs {prevText}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const PERIODS: { key: AktivitaPeriod; label: string }[] = [
  { key: 'day', label: 'Den' },
  { key: 'week', label: 'Týden' },
  { key: 'month', label: 'Měsíc' },
  { key: 'year', label: 'Rok' },
];
const COUNTRIES: { key: AktivitaCountry; label: string }[] = [
  { key: 'all', label: 'Vše' },
  { key: 'cz', label: 'CZ' },
  { key: 'sk', label: 'SK' },
  { key: 'ro', label: 'RO' },
];

export function PeriodGroup({
  value,
  onChange,
}: {
  value: AktivitaPeriod;
  onChange: (p: AktivitaPeriod) => void;
}) {
  return (
    <div className="btn-group btn-group-sm" role="group" aria-label="Období">
      {PERIODS.map((p) => (
        <button
          key={p.key}
          type="button"
          className={`btn btn-outline-secondary${value === p.key ? ' active' : ''}`}
          onClick={() => onChange(p.key)}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

export function CountryGroup({
  value,
  onChange,
}: {
  value: AktivitaCountry;
  onChange: (c: AktivitaCountry) => void;
}) {
  return (
    <div className="btn-group btn-group-sm" role="group" aria-label="Země">
      {COUNTRIES.map((c) => (
        <button
          key={c.key}
          type="button"
          className={`btn btn-outline-secondary${value === c.key ? ' active' : ''}`}
          onClick={() => onChange(c.key)}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}

export interface TrendDataset {
  label: string;
  data: number[];
  borderColor: string;
  backgroundColor?: string;
  fill?: boolean;
  yAxisID?: string;
  borderDash?: number[];
  borderWidth?: number;
}

export interface TrendAxis {
  id: string;
  position: 'left' | 'right';
  title?: string;
  color?: string;
  min?: number;
  max?: number;
  drawOnChartArea?: boolean;
  beginAtZero?: boolean;
}

export function TrendChart({
  labels,
  datasets,
  height,
  axes,
}: {
  labels: string[];
  datasets: TrendDataset[];
  height: number;
  axes?: TrendAxis[];
}) {
  const data = {
    labels,
    datasets: datasets.map((d) => ({
      label: d.label,
      data: d.data,
      borderColor: d.borderColor,
      backgroundColor: d.backgroundColor ?? d.borderColor + '22',
      fill: d.fill ?? false,
      tension: 0.25,
      pointRadius: 0,
      borderWidth: d.borderWidth ?? 2,
      borderDash: d.borderDash,
      yAxisID: d.yAxisID,
    })),
  };
  const scales: any = {
    x: { ticks: { font: { size: 11 }, maxTicksLimit: 12 } },
  };
  if (axes && axes.length) {
    for (const a of axes) {
      scales[a.id] = {
        position: a.position,
        beginAtZero: a.beginAtZero ?? true,
        ...(a.min != null ? { min: a.min } : {}),
        ...(a.max != null ? { max: a.max } : {}),
        grid: { drawOnChartArea: a.drawOnChartArea ?? true },
        ticks: { font: { size: 11 }, ...(a.color ? { color: a.color } : {}) },
        ...(a.title
          ? { title: { display: true, text: a.title, font: { size: 10 }, ...(a.color ? { color: a.color } : {}) } }
          : {}),
      };
    }
  } else {
    scales.y = { beginAtZero: true, ticks: { font: { size: 11 } } };
  }
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'bottom', labels: { font: { size: 11 }, usePointStyle: true, pointStyle: 'line' } },
    },
    scales,
  };
  return (
    <div style={{ height, position: 'relative' }}>
      <Line data={data} options={options} />
    </div>
  );
}
